import { LocationModel } from 'common/data/location/model';
import { AutocompleteResultInterface } from 'common/module/autocomplete/result.interface';
import { AutocompleteSectionInterface } from 'common/module/autocomplete/section.interface';
import { BuildingReviewLandingAutocompleteStatechartEvent } from 'common/module/building-review-landing/autocomplete/statechart.event';
import { BuildingReviewLandingAutocompleteStatechartActionEnum } from 'common/module/building-review-landing/autocomplete/statechart/action.enum';
import { BuildingReviewLandingFormViewStore } from 'common/module/building-review-landing/form/view-store';
import { PromiseCancelable } from 'common/module/promise/cancelable';
import { PromiseCancelableInterface } from 'common/module/promise/cancelable.interface';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { LocationStoreServiceInterface } from 'common/service/location-store/service.interface';

export class BuildingReviewLandingAutocompleteStore {
  /**
   * Fetch form settings cancelable promise
   */
  private fetchAutocompleteLocationsCancelable: PromiseCancelableInterface<LocationModel[]>;

  /**
   * Constructor
   */
  public constructor(
    private autocompleteStatechart: StatechartStoreInterface,
    private locationStore: LocationStoreServiceInterface,
    private landingFormStore: BuildingReviewLandingFormViewStore
  ) {}

  /**
   * @inheritdoc
   */
  public initialize(): void {
    // Attach observers
    this.observers();
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.fetchListItems,
        this.onFetchListItemsActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.cancelCurrentFetch,
        this.onCancelCurrentFetchActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.updateFormField,
        this.onUpdateFormFieldActionAutocompleteStatechart
      );

    this.autocompleteStatechart
      .getEventEmitter()
      .addListener(
        BuildingReviewLandingAutocompleteStatechartActionEnum.resetSelection,
        this.onResetSelectionActionAutocompleteStatechart
      );
  }

  /**
   * Fetch autocomplete locations succeeded
   */
  private onFetchAutocompleteLocationsSucceded = (locations: LocationModel[]) => {
    if (!locations) {
      return;
    }

    this.autocompleteStatechart.transit<AutocompleteSectionInterface[]>({
      event: BuildingReviewLandingAutocompleteStatechartEvent.fetchListItemsSucceeded,
      payload: [
        {
          id: 'communities',
          name: '',
          suggestions: locations.map((location) => ({
            id: location.id,
            text: location.name,
            description: location.path_name,
            sectionId: 'communities',
          })),
        },
      ],
    });
  };

  /**
   * Fetch autocomplete list items
   */
  private onFetchListItemsActionAutocompleteStatechart = (searchString: string) => {
    this.fetchAutocompleteLocationsCancelable = new PromiseCancelable(
      this.locationStore.fetchAll({
        'filter[full_name]': searchString,
        'filter[is_leaf]': '1',
        sort: 'name',
      })
    );

    this.fetchAutocompleteLocationsCancelable.getPromise().then(this.onFetchAutocompleteLocationsSucceded);
  };

  /**
   * Cancel current locations fetch
   */
  private onCancelCurrentFetchActionAutocompleteStatechart = () => {
    this.fetchAutocompleteLocationsCancelable.cancel();
  };

  /**
   * Set location form field
   */
  private onUpdateFormFieldActionAutocompleteStatechart = (result: AutocompleteResultInterface) => {
    this.landingFormStore.setLocation(this.locationStore.getOneById(result.id));
  };

  /**
   * Reset location form field
   */
  private onResetSelectionActionAutocompleteStatechart = () => {
    this.landingFormStore.setLocation(null);
  };
}
